import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext } from 'react-hook-form';
import minusIcon from '@/assets/icons/minus.svg';
import addIcon from '@/assets/icons/add.svg';
import { TextField } from './FormField';
import { FileField } from './FileField';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import { useExistingAdditionalDocuments } from '../ExistingDocumentsContext';
import styles from './ArrayFields.module.scss';

interface AdditionalDocumentsFieldProps {
  namePrefix: string;
}

const AdditionalDocumentsField = ({ namePrefix }: AdditionalDocumentsFieldProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: namePrefix,
  });

  const personPrefix = namePrefix.replace(/\.additionalDocuments$/, '');
  const existingDocs = useExistingAdditionalDocuments(personPrefix);

  return (
    <div className={styles.wrapper}>
      {existingDocs.length > 0 && (
        <div className={styles.entry}>
          <span className="body-sm-semibold text-(--font-heading)">
            {t('openFundRequest.edit.existingAdditionalDocuments')}
          </span>
          {existingDocs.map((doc, i) =>
            doc.uri ? (
              <div key={i} className="flex items-center gap-1">
                <a
                  href={doc.uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="body-sm-link text-(--font-link) block truncate"
                >
                  {doc.name || t('openFundRequest.edit.viewFile')}
                </a>
                <DownloadFileButton uri={doc.uri} fileName={doc.name} />
              </div>
            ) : null
          )}
        </div>
      )}
      {fields.map((field, index) => (
        <div className={styles.entry} key={field.id}>
          <div className={styles.entryHeader}>
            <span className="body-sm-semibold text-(--font-heading)">
              {t('openFundRequest.fields.additionalDocuments')} {index + 1}
            </span>
            <button
              type="button"
              className={styles.removeButton}
              onClick={() => remove(index)}
              aria-label={t('openFundRequest.buttons.removeDocument')}
            >
              <img src={minusIcon} alt="" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
            <TextField
              name={`${namePrefix}.${index}.name` as never}
              label={t('openFundRequest.fields.documentName')}
            />
            <FileField
              name={`${namePrefix}.${index}.file`}
              label={t('openFundRequest.buttons.uploadFile')}
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        className={styles.addButton}
        onClick={() => append({ name: '', file: null })}
      >
        <img src={addIcon} alt="" />
        {t('openFundRequest.buttons.addDocument')}
      </button>
    </div>
  );
};

export default AdditionalDocumentsField;
